
import { useState, useEffect } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function useSubscriptionStatus() {
  const session = useSession();
  const { toast } = useToast();
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('free');

  const fetchSubscriptionStatus = async () => {
    if (session?.user?.id) {
      setIsLoadingStatus(true);
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('subscription_status')
          .eq('id', session.user.id)
          .single();

        if (error) throw error;

        if (profile) {
          const newStatus = profile.subscription_status || 'free';
          if (newStatus !== subscriptionStatus) {
            setSubscriptionStatus(newStatus);
            if (newStatus === 'premium') {
              toast({
                title: "Welcome to Premium!",
                description: "Your premium features are now active.",
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        toast({
          title: "Error",
          description: "Could not fetch subscription status. Please refresh the page.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingStatus(false);
      }
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
    
    const channel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${session?.user?.id}`,
        },
        () => {
          fetchSubscriptionStatus();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session]);

  return { isLoadingStatus, subscriptionStatus };
}
